import { clsx, type ClassValue } from "clsx";
import {
  differenceInHours,
  differenceInMinutes,
  format,
  formatDistanceToNow,
} from "date-fns";
import type { Feature, Polygon as GeoJsonPolygon } from "geojson";
import { twMerge } from "tailwind-merge";
import { id as ID } from "date-fns/locale";
import xss from "xss";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatToMask(format: string): string {
  return format.replace(/[a-zA-Z]/g, "_");
}

export function formatDate(
  date?: string | Date | null,
  option?: { time?: boolean; dayName?: boolean }
) {
  if (!date) return "-";
  const dayName = option?.dayName !== undefined ? option.dayName : true;
  return format(
    date,
    `${dayName ? "EEEE," : ""} d MMMM yyyy ${option?.time ? "HH:mm" : ""}`,
    {
      locale: ID,
    }
  );
}

export function formatDateHuman(date?: Date | string | number | null): string {
  try {
    if (!date) return "-";
    const d = new Date(date);
    const now = new Date();

    const minutes = differenceInMinutes(now, d);
    const hours = differenceInHours(now, d);

    if (minutes < 1) {
      return "Baru saja";
    } else if (minutes < 60) {
      return formatDistanceToNow(d, { addSuffix: true, locale: ID }); // ex: "3 menit yang lalu"
    } else if (hours < 24) {
      return formatDistanceToNow(d, { addSuffix: true, locale: ID }); // ex: "5 jam yang lalu"
    } else {
      return format(d, "d MMMM yyyy, HH:mm", { locale: ID }); // ex: "26 September 2025, 14:30"
    }
  } catch (error) {
    console.error("Invalid date format:", error);
    return "-";
  }
}

export const formatFileName = (name: string) => {
  if (!name) return "Tidak ada nama file";
  const tempName = name?.split("/");
  const newName = tempName[tempName.length - 1];
  return newName;
};

export function formatErrorMessages(
  errorData: Record<string, string[] | string> | string
): string {
  // Kalau langsung string, balikin apa adanya
  if (typeof errorData === "string") {
    return errorData;
  }

  return Object.entries(errorData)
    .map(([field, messages]) => {
      const readableField = field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());

      if (Array.isArray(messages)) {
        return messages.map((msg) => `• ${readableField}: ${msg}`).join("\n");
      }

      return `• ${readableField}: ${messages}`;
    })
    .join("\n");
}

/** Helper: base64URL → Uint8Array (untuk VAPID public key) */
export function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

export function arrayBufferToBase64(buf: ArrayBuffer | null) {
  if (!buf) return "";
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

export function cleanHTML(input: string): string {
  return xss(input, {
    whiteList: {},
    stripIgnoreTag: true, // hapus tag yang ga di whitelist
    stripIgnoreTagBody: ["script", "style"], // buang isi script/style juga
  });
}

export const getCoords = (feature: Feature<GeoJsonPolygon, AreaProperties>) => {
  if (feature.geometry.type === "Polygon") {
    return feature.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
  }
  if (feature.geometry.type === "MultiPolygon") {
    return feature.geometry.coordinates[0][0].map(([lng, lat]: any) => ({
      lat,
      lng,
    }));
  }
  return [];
};

export function getPolygonBounds(coords: { lat: number; lng: number }[]) {
  const bounds = new google.maps.LatLngBounds();

  coords.forEach(({ lat, lng }) => {
    bounds.extend({ lat, lng });
  });

  return bounds;
}

export function getPolygonCenter(coords: any[]) {
  let minLat = coords[0].lat,
    maxLat = coords[0].lat,
    minLng = coords[0].lng,
    maxLng = coords[0].lng;

  coords.forEach(({ lat, lng }) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });

  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };
}

/**
 *
 * @deprecated
 * @param category
 * @returns
 */
export function StatusSubs(category?: string) {
  switch (category) {
    case "DIAJUKAN":
      return "Diajukan";
    case "DIAJUKAN_INSTANSI":
      return "Diajukan Instansi";
    case "SETUJUI_ADMIN_KECAMATAN":
      return "Disetujui Admin Kecamatan";
    case "TOLAK_ADMIN_KECAMATAN":
      return "Ditolak Admin Kecamatan";
    case "SETUJUI_PENYULUH":
      return "Disetujui Penyuluh";
    case "TOLAK_PENYULUH":
      return "Ditolak Penyuluh";
    case "SETUJUI_DINAS": // <- kalau ini typo, harusnya "SETUJUI_DINAS"
      return "Disetujui Dinas";
    case "TOLAK_DINAS":
      return "Ditolak Dinas";
    case "DIJADWALKAN":
      return "Dijadwalkan";
    case "TERKIRIM":
      return "Terkirim";
    case "SELESAI":
      return "Selesai";
    case "TERLAMBAT":
      return "Terlambat";
    case "NONSUBSIDI":
      return "Non Subsidi";
    case "SUBSIDI":
      return "Subsidi";
    default:
      return "-";
  }
}

export function Gender(category?: string) {
  switch (category) {
    case "MALE":
      return "Laki-laki";
    case "FEMALE":
      return "Perempuan";
    default:
      return "-";
  }
}
